import { useMutation } from '@tanstack/react-query';
import { apiFetch } from './client.js';
import type { UploadGrant } from './types.js';

export const ALLOWED_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'application/zip',
] as const;

export type AllowedContentType = (typeof ALLOWED_TYPES)[number];

export const MAX_BYTES = 25 * 1024 * 1024; // 25 MB matches the BE 413 ceiling.

export function validateFile(file: File): { ok: true } | { ok: false; reason: string } {
  if (!(ALLOWED_TYPES as readonly string[]).includes(file.type)) {
    return { ok: false, reason: `Type non autorisé (${file.type || 'inconnu'}).` };
  }
  if (file.size > MAX_BYTES) {
    return {
      ok: false,
      reason: `Fichier trop volumineux (${(file.size / (1024 * 1024)).toFixed(1)} Mo). Max 25 Mo.`,
    };
  }
  return { ok: true };
}

export interface CreateUploadVars {
  tenantId: string;
  file: File;
}

/**
 * Step 1 of the upload flow: ask the BE for a presigned GCS PUT URL.
 * Returns the `UploadGrant` (primaryKey + uploadUrl + objectName + TTL).
 */
export function useCreateUpload() {
  return useMutation<UploadGrant, Error, CreateUploadVars>({
    mutationFn: ({ tenantId, file }) =>
      apiFetch<UploadGrant>('/uploads/manual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          contentType: file.type as AllowedContentType,
          sizeHint: file.size,
          filename: file.name,
        }),
      }),
  });
}

export interface PutToGcsArgs {
  uploadUrl: string;
  file: File;
  signal?: AbortSignal;
  onProgress?: (loaded: number, total: number) => void;
}

/**
 * Step 2 of the upload flow: stream the file bytes to the GCS-signed PUT
 * URL with progress events. Uses XMLHttpRequest because `fetch()`'s upload
 * progress is not implemented in any major browser.
 */
export function putToGcs({ uploadUrl, file, signal, onProgress }: PutToGcsArgs): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`GCS upload failed: ${xhr.status} ${xhr.statusText}`));
    };
    xhr.onerror = () => reject(new Error('GCS upload network error'));
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(file);
  });
}
